import React, { useEffect, useMemo, useState } from "react";
import { Tag } from "lucide-react";

import { Header } from "../layout/Header";
import { CategorySidebar, MobileCategorySidebar } from "../layout/CategorySidebar";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { AuthorCloud, CloudAuthor as CloudTag } from "../quotes/AuthorCloud";
import { ShareLinkButton } from "../layout/ShareLinkButton";
import { TopFiveButton, TOP_COUNT } from "../layout/TopFiveButton";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { getStoredCurrentUser } from "../../lib/app-events";
import ApiService from "../../services/api-service";
import { CurrentUser } from "../../types/user";
import { CATEGORIES, categoryNameFromId } from "../../lib/categories";
import { useUrlCategory, useUrlParam } from "../../lib/url-state";
import { slugify } from "../../lib/slug";

// Slugs of every category name (e.g. "mindset", "personal-growth"). Tags that
// match one are left out of the cloud so it only shows topic tags, not the
// category list again; their tag pages (/tags/mindset) still work.
const categorySlugs = new Set(
  CATEGORIES.flatMap((category: any) => [slugify(String(category?.name || "")), String(category?._id || "")]).filter(Boolean)
);

type TaggedPost = {
  type: string;
  id: number;
  tags?: string[] | null;
  folksonomy_tags?: { name: string }[] | null;
};

// Builds a tag cloud from posts the same way GET /tags/cloud does: a post counts
// once per tag name (creator and community tags combined), and spellings that
// share a slug keep the most-used one.
function tagCloudFromPosts(posts: TaggedPost[]): CloudTag[] {
  const countByName = new Map<string, number>();
  for (const post of posts) {
    const names = new Set<string>([
      ...(Array.isArray(post.tags) ? post.tags : []),
      ...(Array.isArray(post.folksonomy_tags) ? post.folksonomy_tags.map((meta) => meta?.name) : []),
    ].filter((name): name is string => typeof name === "string"));
    for (const name of names) countByName.set(name, (countByName.get(name) ?? 0) + 1);
  }

  const bySlug = new Map<string, CloudTag>();
  for (const [rawName, count] of countByName) {
    const name = rawName.trim();
    const slug = slugify(name);
    if (!slug) continue;
    const entry = bySlug.get(slug);
    if (!entry || count > entry.count) bySlug.set(slug, { name, slug, count });
  }
  return [...bySlug.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

// /tag-cloud - every tag as a word cloud (bigger = more posts), with the
// category sidebar alongside. Picking a category (?category=) narrows the cloud
// to tags on that category's posts, and "Top 5 Tags" (?top=5) to its biggest
// five. Tags named after a category are hidden.
export function TagCloudPage() {
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [tags, setTags] = useState<CloudTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [activeCategory, setActiveCategory, getCategoryHref] = useUrlCategory();
  const categoryName = activeCategory === "all" ? null : categoryNameFromId(activeCategory);
  const [topParam, setTopParam] = useUrlParam<"all" | "5">("top", "all", ["all", "5"]);
  const showTopOnly = topParam === "5";

  useEffect(() => {
    const handleUserChange = (event: Event) => {
      setCurrentUser(event instanceof CustomEvent ? event.detail ?? null : getStoredCurrentUser());
    };
    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => window.removeEventListener("inspirelens:userchange", handleUserChange);
  }, []);

  useEffect(() => {
    document.title = "Tag Cloud · The Deepam";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    // "All" uses the server's cloud; a single category is built from that
    // category's posts, which already carry their tags.
    const request: Promise<CloudTag[]> =
      activeCategory === "all"
        ? ApiService.getTagCloud().then((res: { tags?: CloudTag[] }) => (Array.isArray(res?.tags) ? res.tags : []))
        : ApiService.getAllContentInCategory(activeCategory).then((res: { posts?: TaggedPost[] }) =>
            tagCloudFromPosts(Array.isArray(res?.posts) ? res.posts : [])
          );
    request
      .then((all) => {
        if (cancelled) return;
        setTags(all.filter((tag) => !categorySlugs.has(tag.slug) && !categorySlugs.has(slugify(tag.name))));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [retryToken, activeCategory]);

  // tags is already sorted most-used first, so the top five are its head.
  const shownTags = useMemo(() => (showTopOnly ? tags.slice(0, TOP_COUNT) : tags), [tags, showTopOnly]);
  // Display only: slugs (links, layout) stay the same. The label drops any "#"
  // a tag was saved with ("#experimentalwisdom") and reads better capitalized.
  const displayTags = useMemo(
    () =>
      shownTags.map((tag) => {
        const label = tag.name.replace(/^#+/, "").trim() || tag.name;
        return { ...tag, name: label.charAt(0).toUpperCase() + label.slice(1) };
      }),
    [shownTags]
  );
  const totalPosts = useMemo(() => shownTags.reduce((sum, tag) => sum + tag.count, 0), [shownTags]);
  const tagCountLabel = showTopOnly
    ? `Top ${shownTags.length} of ${tags.length} ${tags.length === 1 ? "tag" : "tags"}`
    : `${tags.length} ${tags.length === 1 ? "tag" : "tags"}`;

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />

      <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Tag className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 shrink-0" aria-hidden="true" />
            Tag Cloud
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            {loading
              ? `Gathering every tag ${categoryName ? `in ${categoryName}` : "on The Deepam"}...`
              : `${categoryName ? `${categoryName}: ` : ""}${tagCountLabel} · ${totalPosts} ${totalPosts === 1 ? "tagged post" : "tagged posts"}. Bigger tags are on more posts - tap one to browse them.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TopFiveButton noun="Tags" active={showTopOnly} onToggle={() => setTopParam(showTopOnly ? "all" : "5")} disabled={loading} />
          <MobileCategorySidebar activeCategory={activeCategory} onCategoryChange={setActiveCategory} getCategoryHref={getCategoryHref} isLoading={loading} label={categoryName ?? "Categories"} />
          <ShareLinkButton title="Tag Cloud" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
        <aside className="hidden md:block md:col-span-1">
          <div className="sticky top-6">
            <CategorySidebar activeCategory={activeCategory} onCategoryChange={setActiveCategory} getCategoryHref={getCategoryHref} isLoading={loading} />
          </div>
        </aside>

        <main className="md:col-span-3 min-w-0">
          {loading ? (
            <Skeleton className="w-full h-[420px] sm:h-[520px] rounded-3xl" />
          ) : error ? (
            <div className="rounded-3xl border border-slate-200 dark:border-neutral-800 py-16 text-center space-y-3">
              <p className="text-slate-700 dark:text-slate-200 font-medium">Couldn't load tags.</p>
              <Button variant="outline" size="sm" onClick={() => setRetryToken((n) => n + 1)}>
                Try again
              </Button>
            </div>
          ) : tags.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 dark:border-neutral-800 py-16 text-center text-slate-600 dark:text-slate-400">
              {categoryName ? `No tags in ${categoryName} yet.` : "No tags yet."}
            </div>
          ) : (
            <AuthorCloud
              authors={displayTags}
              basePath="/tags"
              unit={{ singular: "tagged post", plural: "tagged posts" }}
              label="Tag cloud - larger tags are on more posts"
            />
          )}
        </main>
      </div>
    </div>
  );
}
